import { AfterViewInit, Component, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { BillClusterService, BillViewModel } from '../../../../../Annual-Details/Taxes/bill-cluster.service';
import { Bill } from '../../../../../Annual-Details/Taxes/bill.model';
import { IWeissmanModalComponent } from '../../../../../Compliance/WeissmanModalService';
import { IdpService } from '../idp.service';
import { IdpCard } from './Card/idpDetailCard.component';

import moment from 'moment';

import DetailsInstallmentMessageItem = Weissman.Model.Workflow.DocumentIntake.OcrResponse.Message.DetailsInstallmentMessageItem;
import { cloneDeep, flatMap, forEach, maxBy, minBy, some } from 'lodash';
import { Payment } from 'src/app/Annual-Details/Taxes/payment.model';
import { FeatureFlagsService } from 'src/app/Common/FeatureFlags/feature-flags-service';

export interface IdpBillDetailModalParams {
    confidence: string;
    idpResults: Weissman.Model.Workflow.DocumentIntake.OcrResponse.API.TaxBillAPIResponse;
    billDetail: BillViewModel;
    disableApplyAll: boolean;
}

export interface IdpBillDetailModalResult {
    bills: Bill[];
    applyAll: boolean;
}

@Component({
    selector: 'idp-bill-detail-modal',
    templateUrl: './idpBillDetailModal.component.html',
    styleUrls: ['./idpBillDetailModal.component.scss'],
    standalone: false,
})
export class IdpBillDetailModalComponent
    implements OnInit, AfterViewInit, IWeissmanModalComponent<IdpBillDetailModalParams, IdpBillDetailModalResult>
{
    constructor(
        private readonly _bsModalRef: BsModalRef,
        private readonly _billClusterService: BillClusterService,
        private readonly _featureFlagsService: FeatureFlagsService,
        private readonly _idpService: IdpService,
    ) {}

    params: IdpBillDetailModalParams;
    result: IdpBillDetailModalResult;

    billCard: IdpCard;
    payments: IdpCard[] = [];

    private _billDetail: BillViewModel;
    private _idpResults: Weissman.Model.Workflow.DocumentIntake.OcrResponse.API.TaxBillAPIResponse;

    get title(): string {
        return `Detected Values: ${this.params?.confidence} Confidence`;
    }

    get atLeastOneRowSelected(): boolean {
        return some(this.payments, (payment) => some(payment.details, 'apply'));
    }

    get isCheckboxesEnabled(): boolean {
        return this._featureFlagsService.featureFlags.idpModalCheckboxes;
    }

    ngOnInit(): void {
        this._billDetail = this.params.billDetail;
        this._idpResults = this.params.idpResults;

        const bill = cloneDeep(this._billDetail.model[0]);

        this.billCard = {
            title: 'Bill',
            total: { name: 'Bill Amt', value: this._idpResults.totalAmount },
            details: [
                // { name: 'Assoc. Revision', value: this._getAssocRevisionDesc(bill), type: 'string' },
                // { name: 'Payment Option', value: this._getBillPaymentOptionName(bill), type: 'string' }
                { name: 'Direct Asmt', value: this._idpResults.directAssessmentAmount || 0, type: 'currency' },
            ],
        };

        // If the PropertyPoint bill has one payment and there is no canonical installment for just one payment,
        // use the canonical bill's totalAmount due and the earliest payment's due date within the canonical bill.
        // This is basically paying the full amount due by the first payment due date.
        if (bill.payments.length === 1 && !some(this._idpResults.installments, (x) => x.details.length === 1)) {
            const idpPayments = flatMap(this._idpResults.installments, 'details') as DetailsInstallmentMessageItem[];
            const idpPayment = minBy(idpPayments, (x) => new Date(x.dueDate));

            if (!idpPayment) return;

            bill.payments[0].dueDate = idpPayment.dueDate;
            bill.payments[0].grossPayment = this._idpResults.totalAmount;
            bill.payments[0].paymentAmount = this._billClusterService.paymentPropChanged(bill.payments[0]);

            this.payments = [
                {
                    title: 'Payment 1 of 1',
                    total: { name: 'Payment Amt', value: bill.payments[0].paymentAmount },
                    details: [
                        {
                            name: 'Base Pay Amt',
                            value: this._idpResults.totalAmount,
                            type: 'currency',
                        },
                        {
                            name: 'Due Date',
                            value: idpPayment.dueDate,
                            type: 'date',
                        },
                    ],
                },
            ];

            return;
        }

        const installment = this._idpResults.installments.find((x) => x.details.length === bill.payments.length);
        let discountIndex = 0;
        if (bill.payments.length === 1) {
            // if bill payment has one payment, find installment with one detail
            // find the payment that matches the due date
            const payment = installment?.details.find((x) =>
                moment(x.dueDate).isSame(moment(bill.payments[0].dueDate)),
            );
            discountIndex = payment?.discount.findIndex((x) => x.dueDate === bill.payments[0].dueDate) || 0;
        }
        // if the bill payment option has multiple payments, find the installment with the same number of details
        // we need to show all the payments available, regardless of due date
        bill.payments.forEach((payment: Payment, pmtIdx: number) => {
            const ocrPayment = installment?.details[pmtIdx];

            if (!ocrPayment) return;

            payment.grossPayment = ocrPayment?.amount || 0;
            payment.discountAmount = -this._idpService.getDiscount(ocrPayment, discountIndex);
            payment.interestAmount = ocrPayment?.interest[discountIndex]?.interestAmount || 0;
            payment.dueDate = ocrPayment?.discount[discountIndex]?.dueDate || ocrPayment.dueDate;
            payment.penaltyAmount = this._idpService.getValidIDPPenalty(ocrPayment?.penalty, payment.dueDate);
            payment.paymentAmount = this._billClusterService.paymentPropChanged(payment);

            this.payments.push({
                title: `Payment ${pmtIdx + 1} of ${installment.details.length}`,
                total: { name: 'Payment Amt', value: payment.paymentAmount },
                details: this._idpService.cardValues
                    .filter((card) => {
                        if (card.name === 'Penalty') {
                            return payment.penaltyAmount > 0;
                        }

                        return true;
                    })
                    .map((card) => ({
                        name: card.name,
                        value: payment[card.field],
                        type: card.type,
                        field: card.field,
                    })),
            });
        });
    }

    ngAfterViewInit(): void {
        const modalContainer = document.querySelector('modal-container');
        if (modalContainer) {
            modalContainer.classList.add('no-modal-backdrop');
        }
    }

    applyAll(): void {
        this._apply();
    }

    applySome(): void {
        this._apply(this.payments);
    }

    cancel(): void {
        this._bsModalRef.hide();
    }

    private _apply(paymentsFromModal?: IdpCard[]): void {
        this._idpService.mapBillVMFromIDP(this._billDetail, this._idpResults, paymentsFromModal);

        this.result = {
            bills: this._billDetail.model,
            applyAll: !paymentsFromModal,
        };

        this._bsModalRef.hide();
    }

    private _getAssocRevisionDesc(bill: Bill): string {
        const yearRevision = this.params.billDetail.yearRevisions.find(
            (x) => x.annualAssessmentID === bill.annualAssessmentID,
        );

        return yearRevision?.revisionDesc || '';
    }

    private _getBillPaymentOptionName(bill: Bill): string {
        if (!bill.collectorPaymentOptionID || !bill.collectorPaymentOptions) {
            return '';
        } else if (bill.collectorPaymentOptions.length === 0) {
            return bill.collectorPaymentOption.name || 'N/A';
        }

        const option = bill.collectorPaymentOptions.find(
            (x) => x.collectorPaymentOptionID === bill.collectorPaymentOptionID,
        );

        return option?.name || 'N/A';
    }
}
